"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.residentStatusWarningSchema = exports.RESIDENT_STATUS_WARNING_KINDS = void 0;
exports.residentStatusWarning = residentStatusWarning;
exports.residentStatusWarningText = residentStatusWarningText;
/**
 * `RPM-085` — what the mirror says about whether a resident is still here, as one warning (`J-307`).
 *
 * ONE READING OF THE THREE STATUS FACTS, used by the server and by the screen. The roster response
 * carries the warning as a first-class field (the block's API row: "The workup contract carries the
 * warning as a first-class field, not as a note"), and the entry's trail records the same warning when
 * the nurse proceeds past it. Two readings of the same columns could disagree about a resident on
 * the boundary, so there is one, here.
 *
 * DEATH OUTRANKS DISCHARGE. `RPM-097`'s option line reads "**Deceased** with `deceased_date` where one
 * is set, otherwise the `status` with `discharge_date`", and `RPM-085` row 3 makes Deceased "the
 * strongest of the three". So a `deceased_date` decides it whatever `status` says.
 *
 * NOTHING BRANCHES ON IT. The warning is shown and recorded; it refuses no reading, suppresses no
 * obligation and changes no lane or route (`RPM-085`'s MUST, `Q-RPM-13`).
 */
const zod_1 = require("zod");
/** The mirror's `status` value of a resident living in the building (`external.ts`). */
const CURRENT = "Current";
const DISCHARGED = "Discharged";
/** The three warnings, weakest last. `STATUS_UNKNOWN`: the mirror does not say she is Current. */
exports.RESIDENT_STATUS_WARNING_KINDS = ["DECEASED", "DISCHARGED", "STATUS_UNKNOWN"];
exports.residentStatusWarningSchema = zod_1.z
    .object({
    kind: zod_1.z.enum(exports.RESIDENT_STATUS_WARNING_KINDS),
    /** The day the status took effect, as the mirror holds it, or null where it holds none. */
    date: zod_1.z.string().nullable(),
})
    .meta({
    id: "ResidentStatusWarning",
    description: "What the mirror says about a resident who may no longer be in the building (RPM-085)",
});
/** The warning the mirror's status facts call for, or null for a Current resident. */
function residentStatusWarning(facts) {
    if (facts.deceasedDate)
        return { kind: "DECEASED", date: facts.deceasedDate };
    if (facts.status === CURRENT)
        return null;
    if (facts.status === DISCHARGED)
        return { kind: "DISCHARGED", date: facts.dischargeDate };
    return { kind: "STATUS_UNKNOWN", date: null };
}
/** The day of a mirror date: the warning states a day, never an instant. */
const day = (value) => (value ? value.slice(0, 10) : null);
/**
 * The warning in words, as the trail records it and as the banner states it: the status and its day
 * (`RPM-085`'s Operator surface row, "Discharged 2026-09-08").
 */
function residentStatusWarningText(warning) {
    const on = day(warning.date);
    switch (warning.kind) {
        case "DECEASED":
            return on ? `Deceased ${on}` : "Deceased, date not recorded";
        case "DISCHARGED":
            return on ? `Discharged ${on}` : "Discharged, date not recorded";
        case "STATUS_UNKNOWN":
            return "The mirror does not say this resident is current";
    }
}
//# sourceMappingURL=resident-status.js.map